// Edge profiles we cut: cross-sections drawn to scale (10 units = 1 cm) for the website
export const EDGE_TYPES = [
  ["all", "All types"],
  ["single", "Single"],
  ["laminated", "Laminated"],
];

// Section: top surface at y=40, 3 cm slab ends at y=70, laminated builds to 6 cm (y=100)
// with the glued strip 3.2 cm deep behind the face (x=118..150).
export const EDGE_PROFILES = [
  {
    slug: "eased",
    name: "Eased",
    type: "single",
    tag: "Clean, square and softened",
    body: "A square edge with the sharp corners lightly relieved. The most popular profile for modern kitchens — it shows the full thickness of the stone and pairs with any cabinet style.",
    best: "Modern and transitional kitchens, waterfall islands, thin porcelain.",
    path: "M0,40 H147 Q150,40 150,43 V67 Q150,70 147,70 H0 Z",
  },
  {
    slug: "half-bevel",
    name: "Half Bevel",
    type: "single",
    tag: "A crisp 45° chamfer on top",
    body: "The top corner is cut back at 45 degrees for a fine highlight line along the edge while the face stays flat. Adds definition without adding fuss.",
    best: "Contemporary kitchens, dark stones where the chamfer catches light.",
    path: "M0,40 H141 L150,49 V70 H0 Z",
  },
  {
    slug: "bevel",
    name: "Bevel",
    type: "single",
    tag: "A deeper chamfer for a bolder line",
    body: "A wider 45-degree cut than the half bevel, giving a strong, architectural shadow line along the counter.",
    best: "Traditional and craftsman kitchens, thick 3 cm granite.",
    path: "M0,40 H136 L150,54 V70 H0 Z",
  },
  {
    slug: "quarter-round",
    name: "Quarter Round",
    type: "single",
    tag: "A soft radius on the top corner",
    body: "The top corner is rounded to a quarter circle and the bottom stays square, so the counter reads soft from above but keeps a clean drip edge.",
    best: "Family kitchens, bathrooms, anywhere hips and hands meet the edge.",
    path: "M0,40 H134 A16,16 0 0 1 150,56 V70 H0 Z",
  },
  {
    slug: "half-round",
    name: "Half Round",
    type: "single",
    tag: "The classic full bullnose",
    body: "The whole edge is rounded into a half circle — no corners at all. Smooth to the touch and forgiving in busy homes.",
    best: "Kitchens with young children, bar tops, classic homes.",
    path: "M0,40 H135 A15,15 0 0 1 135,70 H0 Z",
  },
  {
    slug: "half-bullnose",
    name: "Half Bullnose",
    type: "single",
    tag: "Rounded on top, square below",
    body: "A large radius rolls the top surface over the edge while the underside stays flat, so the stone looks thicker and rounder from standing height.",
    best: "Traditional kitchens, granite and marble.",
    path: "M0,40 H126 A24,24 0 0 1 150,64 V70 H0 Z",
  },
  {
    slug: "ogee",
    name: "Ogee",
    type: "single",
    tag: "The classic S-curve",
    body: "A concave curve flowing into a convex one — the profile of traditional mouldings. Elegant and detailed, it draws the eye along the counter.",
    best: "Traditional, French country and formal kitchens; marble and quartzite.",
    path: "M0,40 H128 C140,40 136,54 146,56 C152,58 150,66 150,70 H0 Z",
  },
  {
    slug: "cove-ogee",
    name: "Cove Ogee",
    type: "single",
    tag: "A deep cove above a rounded lip",
    body: "A scooped cove at the top steps down to a round bead at the bottom, giving more shadow and depth than a standard ogee.",
    best: "Ornate kitchens, islands and bars where the edge is a feature.",
    path: "M0,40 H150 C136,42 136,56 144,58 A6,6 0 0 1 148,70 H0 Z",
  },
  {
    slug: "triple-pencil",
    name: "Triple Pencil",
    type: "single",
    tag: "Three fine rounds down the face",
    body: "Three small beads run along the edge face for a subtle decorative texture that still reads as a straight edge from a distance.",
    best: "Transitional kitchens, vanities, fireplace surrounds.",
    path: "M0,40 H145 A5,5 0 0 1 145,50 A5,5 0 0 1 145,60 A5,5 0 0 1 145,70 H0 Z",
  },
  {
    slug: "chiseled",
    name: "Chiseled",
    type: "single",
    tag: "Hand-broken, rustic texture",
    body: "The edge is chipped and broken by hand for a natural, quarried look, then sealed. No two feet of it are the same.",
    best: "Rustic kitchens, outdoor kitchens, hearths and fireplaces in granite or soapstone.",
    path: "M0,40 H145 L151,44 L146,49 L152,54 L147,59 L151,64 L147,70 H0 Z",
  },
  {
    slug: "pencil",
    name: "Pencil",
    type: "single",
    tag: "A soft, small round top and bottom",
    body: "Both corners are rounded to a pencil-line radius, so the edge reads slightly softer than eased while the face stays flat. Comfortable to lean on and easy to keep clean.",
    best: "Family kitchens, bathrooms, thinner 2 cm stone.",
    path: "M0,40 H144 A6,6 0 0 1 150,46 V64 A6,6 0 0 1 144,70 H0 Z",
  },
  {
    slug: "cove",
    name: "Cove",
    type: "single",
    tag: "A gentle scoop into the face",
    body: "A shallow concave sweep from the top surface down the face. It catches light along the hollow and gives a quiet, traditional line without the ogee's S-curve.",
    best: "Traditional and farmhouse kitchens, painted cabinetry.",
    path: "M0,40 H150 A18,18 0 0 0 150,70 H0 Z",
  },
  {
    slug: "double-round-over",
    name: "Double Quarter Round",
    type: "single",
    tag: "Quarter rounds top and bottom",
    body: "A quarter-round on the top corner and another on the bottom, leaving a short flat on the face. Softer than a single quarter round and less bulky than a full bullnose.",
    best: "Islands and bar tops where people sit or lean.",
    path: "M0,40 H138 A12,12 0 0 1 150,52 V58 A12,12 0 0 1 138,70 H0 Z",
  },
  {
    slug: "laminated-eased",
    name: "Laminated Eased",
    type: "laminated",
    tag: "A 6 cm square edge from 3 cm stone",
    body: "A strip of the same slab is glued under the front edge, then the two are polished as one so the counter reads as a thick 6 cm block.",
    best: "Islands and waterfall ends that want a chunky, modern look.",
    path: "M0,40 H147 Q150,40 150,43 V97 Q150,100 147,100 H118 V70 H0 Z",
    seam: "M118,70 H150",
  },
  {
    slug: "laminated-bullnose",
    name: "Laminated Bullnose",
    type: "laminated",
    tag: "A full 6 cm round",
    body: "Two thicknesses laminated and rounded into one large half circle — soft, substantial and traditional.",
    best: "Classic kitchens and bar tops in granite.",
    path: "M0,40 H120 A30,30 0 0 1 120,100 H118 V70 H0 Z",
    seam: "M118,70 H148",
  },
  {
    slug: "laminated-bevel",
    name: "Laminated Bevel",
    type: "laminated",
    tag: "A thick edge with a chamfered top",
    body: "The built-up 6 cm edge finished with a 45-degree bevel along the top for a strong, tailored line.",
    best: "Large islands, commercial counters, dark stones.",
    path: "M0,40 H138 L150,52 V100 H118 V70 H0 Z",
    seam: "M118,70 H150",
  },
  {
    slug: "laminated-ogee",
    name: "Laminated Ogee",
    type: "laminated",
    tag: "A tall S-curve with real presence",
    body: "The ogee curve cut into a laminated 6 cm edge, so the sweep of the profile is twice the size and casts a deep shadow line.",
    best: "Formal kitchens, dining islands, marble and quartzite.",
    path: "M0,40 H124 C142,40 134,62 146,66 C154,70 150,92 150,100 H118 V70 H0 Z",
    seam: "M118,70 H144",
  },
  {
    slug: "mitered",
    name: "Mitered",
    type: "laminated",
    tag: "A seamless thick edge, veins running over",
    body: "The face strip is cut at 45 degrees and joined to the top at the corner, so the pattern of the stone folds over the edge with no visible seam on top. Any apparent thickness is possible.",
    best: "Waterfall islands, thin porcelain and quartz made to look 6–10 cm thick.",
    path: "M0,40 H150 V100 H118 V70 H0 Z",
    seam: "M150,40 L120,70 V100",
  },
  {
    slug: "dupont",
    name: "Dupont",
    type: "single",
    optional: true,
    tag: "A square top over a cove step",
    body: "The top stays crisp and square, then the face steps back through a cove for a tailored, architectural line. A favourite on traditional and transitional kitchens.",
    best: "Traditional and transitional kitchens, painted cabinetry.",
    path: "M0,40 H150 V47 C150,57 140,55 140,63 V70 H0 Z",
  },
  {
    slug: "roman-ogee",
    name: "Roman Ogee",
    type: "single",
    optional: true,
    tag: "A rounded S with a soft return",
    body: "A round-over rolls into a gentle cove for a fuller, softer S-curve than a classic ogee — ornate without being sharp.",
    best: "French country, formal and traditional kitchens; marble.",
    path: "M0,40 H130 A8,8 0 0 1 138,48 A9,9 0 0 0 150,58 V70 H0 Z",
  },
  {
    slug: "bevel-bullnose",
    name: "Bevel Bullnose",
    type: "single",
    optional: true,
    tag: "A chamfered top over a round",
    body: "A crisp bevel catches the light along the top while the lower corner rolls into a bullnose — the clean line of a bevel with the comfort of a round.",
    best: "Transitional kitchens, islands and bar tops.",
    path: "M0,40 H140 L150,50 V56 A14,14 0 0 1 136,70 H0 Z",
  },
  {
    slug: "ogee-bullnose",
    name: "Ogee Bullnose",
    type: "single",
    optional: true,
    tag: "An ogee flowing into a full round",
    body: "The ogee's S-curve at the top melts into a rounded bottom for a rich, decorative edge that still feels soft to the touch.",
    best: "Formal kitchens, vanities and fireplace surrounds.",
    path: "M0,40 H128 C140,40 136,52 146,54 A10,10 0 0 1 140,70 H0 Z",
  },
];

export const edgeTypeLabel = (t) =>
  t === "laminated" ? "Laminated · 6 cm" : "Single · 2–3 cm";

export const EDGE_LINK = (name, stone = "") =>
  `/contact?topic=quote&edge=${encodeURIComponent(name)}${
    stone ? `&color=${encodeURIComponent(stone)}` : ""
  }`;

// Office edge-style names (quotes, drawings) → the profile drawn on the website.
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const ALIASES = {
  square: "eased",
  squareeased: "eased",
  straight: "eased",
  "12bevel": "half-bevel",
  fullbevel: "bevel",
  bv: "bevel",
  "14round": "quarter-round",
  "14roundover": "quarter-round",
  roundover: "quarter-round",
  qro: "quarter-round",
  bullnose: "half-round",
  fullbullnose: "half-round",
  fb: "half-round",
  hb: "half-bullnose",
  demibullnose: "half-bullnose",
  og: "ogee",
  ogeecove: "cove-ogee",
  chiselled: "chiseled",
  chisel: "chiseled",
  ce: "chiseled",
  rockface: "chiseled",
  pen: "pencil",
  doubleroundover: "double-round-over",
  double14roundover: "double-round-over",
  doublequarterround: "double-round-over",
  dro: "double-round-over",
  mitred: "mitered",
  miter: "mitered",
  mitre: "mitered",
  mitered2cm: "mitered",
  me: "mitered",
};
export const edgeProfileFor = (name) => {
  const key = norm(name);
  if (!key) return null;
  const slug =
    EDGE_PROFILES.find((p) => norm(p.slug) === key || norm(p.name) === key)?.slug ||
    ALIASES[key];
  return slug ? EDGE_PROFILES.find((p) => p.slug === slug) : null;
};

// The full drawable catalogue, keyed by slug (used by the office editor + resolver).
export const catalogBySlug = Object.fromEntries(EDGE_PROFILES.map((p) => [p.slug, p]));

// Merge the office-managed edge list (config.edges.profiles) onto the drawable catalogue.
// Empty/unset → the default set (everything except optional shapes).
export const resolveEdges = (cfg) => {
  const list = Array.isArray(cfg?.profiles) ? cfg.profiles : null;
  if (!list || !list.length) return EDGE_PROFILES.filter((p) => !p.optional);
  return list
    .filter((e) => e && e.enabled !== false)
    .map((e) => {
      const base = catalogBySlug[e.base || e.slug];
      if (!base) return null;
      return {
        ...base,
        slug: e.slug || base.slug,
        name: e.name || base.name,
        type: e.type || base.type,
        tag: e.tag ?? base.tag ?? "",
        body: e.body ?? base.body ?? "",
        best: e.best ?? base.best ?? "",
        photo: e.photo || base.photo || "",
      };
    })
    .filter(Boolean);
};
